import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { Switch } from "@/ui/components/ui/switch";
import { Separator } from "@/ui/components/ui/separator";
import { useGetMapOverlayVersions } from "@/map/api/queries/overlay.queries";
import { MapOverlayVersionSelector } from "@/map/components/map-overlay";
import PolygonControls from "@/polygon/components/polygon-controls.tsx";
import { useMapProviderStore } from "@/map/stores/useMapProviderStore";
import {
  LIVE_DELAY_CHOICES,
  useLiveDelayStore,
  type LiveDelayMs,
} from "@/map/stores/useLiveDelayStore";

export interface MapSettings {
  locationId?: number;
  overlayEnabled: boolean;
  onOverlayEnabledChange: (enabled: boolean) => void;
  overlayVersion?: string;
  onOverlayVersionChange: (version: string) => void;
  selectedPolygonIds: number[];
  onSelectedPolygonIdsChange: (ids: number[]) => void;
}

const SettingRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <span className="text-sm">{label}</span>
    {children}
  </div>
);

/**
 * Map configuration hosted in the right-hand Settings drawer (relocated from the
 * floating on-map controls): base-map provider, overlay version + toggle, and
 * geofence polygon selection. State is owned by the page and passed to MapView.
 */
const MapSettingsPanel = ({
  locationId,
  overlayEnabled,
  onOverlayEnabledChange,
  overlayVersion,
  onOverlayVersionChange,
  selectedPolygonIds,
  onSelectedPolygonIdsChange,
}: MapSettings) => {
  const { t } = useTranslation("map");
  const provider = useMapProviderStore((s) => s.provider);
  const setProvider = useMapProviderStore((s) => s.setProvider);
  const liveDelayMs = useLiveDelayStore((s) => s.liveDelayMs);
  const setLiveDelayMs = useLiveDelayStore((s) => s.setLiveDelayMs);

  const { data: versionsData, isLoading: isVersionsLoading } = useGetMapOverlayVersions({
    location_id: locationId!,
  });
  const versions = versionsData?.data?.versions ?? [];
  const activeVersion = versions.find((v) => v.status === "active");

  // Default to the active version once versions load.
  useEffect(() => {
    if (!overlayVersion && activeVersion) onOverlayVersionChange(activeVersion.version);
  }, [activeVersion, overlayVersion, onOverlayVersionChange]);

  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("settingsPanel.baseMap")}</div>
      <SettingRow label={t("settingsPanel.provider")}>
        <select
          className="rounded-md border bg-background px-2 py-1 text-xs"
          value={provider}
          onChange={(e) => setProvider(e.target.value as "google" | "libre")}
        >
          <option value="google">Google</option>
          <option value="libre">Libre</option>
        </select>
      </SettingRow>

      <Separator className="my-2" />

      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("settingsPanel.liveVideo")}</div>
      <SettingRow label={t("settingsPanel.stayBehindBy")}>
        <select
          className="rounded-md border bg-background px-2 py-1 text-xs"
          value={liveDelayMs}
          onChange={(e) => setLiveDelayMs(Number(e.target.value) as LiveDelayMs)}
        >
          {LIVE_DELAY_CHOICES.map((ms) => (
            <option key={ms} value={ms}>
              {t("settingsPanel.delayOption", { minutes: formatNumber(ms / 60_000) })}
            </option>
          ))}
        </select>
      </SettingRow>
      {/* The trade, stated plainly: this is not latency for its own sake. Clips
          only become fetchable 17-68s after they end, so a player closer to the
          edge than that runs out of material and freezes. */}
      <p className="pb-2 text-xs text-muted-foreground">{t("settingsPanel.liveDelayHelp")}</p>

      <Separator className="my-2" />

      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("settingsPanel.mapOverlay")}</div>
      {!locationId ? (
        <p className="py-2 text-xs text-muted-foreground">{t("settingsPanel.selectLocation")}</p>
      ) : versions.length === 0 ? (
        <p className="py-2 text-xs text-muted-foreground">
          {isVersionsLoading
            ? t("settingsPanel.loadingVersions")
            : t("settingsPanel.noOverlay")}
        </p>
      ) : (
        <div className="flex flex-col gap-2 py-1">
          <SettingRow label={t("settingsPanel.showOverlay")}>
            <Switch checked={overlayEnabled} onCheckedChange={onOverlayEnabledChange} />
          </SettingRow>
          <MapOverlayVersionSelector
            versions={versions}
            selectedVersion={overlayVersion}
            onVersionChange={onOverlayVersionChange}
            isLoading={isVersionsLoading}
          />
        </div>
      )}

      <Separator className="my-2" />

      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("settingsPanel.geofences")}</div>
      <div className="py-1">
        <PolygonControls
          selectedPolygonIds={selectedPolygonIds}
          onSelectionChange={onSelectedPolygonIdsChange}
          locationId={locationId}
        />
      </div>
    </div>
  );
};

export default MapSettingsPanel;
